import React, { useState, useEffect } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';
import {
  ArrowLeft, Crown, Zap, Coins, Clock, CreditCard,
  Check, ExternalLink, Loader2, RefreshCw, AlertCircle,
  Shield, Sparkles, Cloud, Database, ChevronRight, FolderSync, Minus, CheckCircle
} from 'lucide-react';
import { UserProfile, UserSubscription, SUBSCRIPTION_LIMITS } from '../types';
import { usePlans, type SubscriptionPlan } from '../hooks/usePlans';
import { subscribeToStorageUsage, recalculateStorageUsage, StorageUsage, migrateStorageStructure, checkNeedsMigration } from '../utils/storage';
import { registerExistingFiles } from '../utils/fileRegistry';
import { getEffectiveTier } from '../utils/subscriptionAccess';
import { PayWithQr } from '../components/PayWithQr';
import { grantIsActive, type ProGrant } from '../utils/proGrant';
import { ProfileMenu } from '../components/ProfileMenu';
import { formatRelativeTime } from '../utils/workspaceLibrary';
import { SettingsNav } from '../components/settings/SettingsNav';
import { CREDIT_PACKAGES, PRO_DISCOUNT_PERCENT, PRO_LIST_USDC, PRO_MONTHLY_USDC, formatUsdc } from '../utils/pricing';
import { BetaTag, BrandMark } from '../components/BrandMark';

interface SubscriptionPageProps {
  user: UserProfile;
  subscription?: UserSubscription | null;
  /** Pro access given to this address by hand, without payment */
  proGrant?: ProGrant | null;
  onBack: () => void;
  onSubscriptionUpdate?: () => void;
  onAnalytics?: () => void;
  onSettings?: () => void;
  onSignOut?: () => void;
  onTerms?: () => void;
  onPrivacy?: () => void;
  onAbout?: () => void;
}

// The price and the packages are the app's one pricing (utils/pricing.ts)

export const SubscriptionPage: React.FC<SubscriptionPageProps> = ({
  user,
  subscription,
  proGrant,
  onBack,
  onSubscriptionUpdate,
  onAnalytics,
  onSettings,
  onSignOut,
  onTerms,
  onPrivacy,
  onAbout,
}) => {
  usePageMeta(PAGE_META.subscription);

  const [selectedPackage, setSelectedPackage] = useState(2); // Default to 6 months
  const [storageUsage, setStorageUsage] = useState<StorageUsage>({ totalBytes: 0, lastUpdated: Date.now(), fileCount: 0 });
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateProgress, setRecalculateProgress] = useState({ current: 0, total: 0 });
  
  // Migration state
  const [needsMigration, setNeedsMigration] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ current: 0, total: 0, status: '' });
  const [migrationResult, setMigrationResult] = useState<{ migratedFiles: number; errors: string[] } | null>(null);
  
  // File registry state
  const [isRegisteringFiles, setIsRegisteringFiles] = useState(false);
  const [registerProgress, setRegisterProgress] = useState({ current: 0, total: 0, status: '' });
  const [registerResult, setRegisterResult] = useState<{ registered: number; skipped: number; errors: string[] } | null>(null);
  
  // Paying: the QR panel is open for the chosen package; then what was just paid
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState<{ signature: string; credits: number } | null>(null);

  const tier = getEffectiveTier(subscription || undefined, proGrant);
  const isPro = tier === 'pro' || tier === 'enterprise';
  const given = grantIsActive(proGrant) ? proGrant : null;
  const currentCredits = subscription?.credits || 0;
  const limits = SUBSCRIPTION_LIMITS[tier];

  // Fetch dynamic plans from admin-managed database
  const { plans: dynamicPlans, isLoading: plansLoading } = usePlans();

  // Calculate remaining days in current period
  const getRemainingDays = () => {
    if (!subscription?.currentCreditExpiresAt) return 0;
    const now = Date.now();
    const expiry = subscription.currentCreditExpiresAt;
    if (expiry <= now) return 0;
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  const remainingDays = getRemainingDays();
  
  // Calculate total remaining time (current period + future credits)
  // Each credit = 30 days
  const getTotalRemainingDays = () => {
    const currentPeriodDays = remainingDays;
    const futureCreditsdays = currentCredits * 30;
    return currentPeriodDays + futureCreditsdays;
  };
  
  const totalRemainingDays = getTotalRemainingDays();
  const inRouter = useInRouterContext();

  // Subscribe to real-time storage usage updates
  useEffect(() => {
    if (!user?.sub) return;
    
    const unsubscribe = subscribeToStorageUsage(user.sub, (usage) => {
      setStorageUsage(usage);
    });
    
    return () => unsubscribe();
  }, [user?.sub]);

  // Check if storage migration is needed
  useEffect(() => {
    const checkMigration = async () => {
      if (!user?.sub || !isPro) return;
      const needs = await checkNeedsMigration(user.sub);
      setNeedsMigration(needs);
    };
    checkMigration();
  }, [user?.sub, isPro]);

  // A day as these pages write it: 29 Oct 2026
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleRecalculateStorage = async () => {
    if (!user?.sub || isRecalculating) return;
    
    setIsRecalculating(true);
    setRecalculateProgress({ current: 0, total: 0 });
    
    try {
      await recalculateStorageUsage(user.sub, (current, total) => {
        setRecalculateProgress({ current, total });
      });
    } catch (error) {
      console.error('Recalculation failed:', error);
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleMigrateStorage = async () => {
    if (!user?.sub || isMigrating) return;
    
    // Prompt for workspace name (use default if cancelled)
    const workspaceName = prompt('Enter a name for your migrated storage folder:', 'My Workspace') || 'My_Workspace';
    
    setIsMigrating(true);
    setMigrationProgress({ current: 0, total: 0, status: 'Starting migration...' });
    setMigrationResult(null);
    
    try {
      const result = await migrateStorageStructure(
        user.sub, 
        workspaceName,
        (current, total, status) => {
          setMigrationProgress({ current, total, status });
        }
      );
      
      setMigrationResult(result);
      
      if (result.migratedFiles > 0) {
        // Refresh storage usage after migration
        await recalculateStorageUsage(user.sub);
        // Check if migration is still needed
        const stillNeeds = await checkNeedsMigration(user.sub);
        setNeedsMigration(stillNeeds);
      }
    } catch (error) {
      console.error('Migration failed:', error);
      setMigrationResult({ migratedFiles: 0, errors: [(error as Error).message] });
    } finally {
      setIsMigrating(false);
    }
  };

  const handleRegisterExistingFiles = async () => {
    if (!user?.sub || isRegisteringFiles) return;
    
    setIsRegisteringFiles(true);
    setRegisterProgress({ current: 0, total: 0, status: 'Starting...' });
    setRegisterResult(null);
    
    try {
      const result = await registerExistingFiles(
        user.sub,
        (current, total, status) => {
          setRegisterProgress({ current, total, status });
        }
      );
      
      setRegisterResult(result);
    } catch (error) {
      console.error('Registration failed:', error);
      setRegisterResult({ registered: 0, skipped: 0, errors: [(error as Error).message] });
    } finally {
      setIsRegisteringFiles(false);
    }
  };

  // How full the cloud storage is. (Only shown on a paid plan, where the limit is not zero.)
  const storageRatio = (storageUsage.totalBytes / (1024 * 1024)) / limits.maxStorageMB;
  const storagePercent = Math.min(100, storageRatio * 100) || 0;

  return (
    <div className="fixed inset-0 z-[200] bg-cream overflow-hidden">
      {/* A column as tall as the window: the bar and the footer stay, the content scrolls between them */}
      <div className="h-full flex flex-col">
        {/* The bar every page has: the mark, the way back, where you are; the account on the right */}
        <header className="ns-pagebar px-4">
          <div className="w-full flex items-center gap-3">
            <span className="flex items-center gap-2.5 shrink-0" aria-hidden="true">
              <BrandMark size={32} />
              <span className="text-[1.0625rem] font-bold tracking-[-0.02em] hidden md:inline">Noodle Studio</span>
              <BetaTag className="hidden md:inline-flex" />
            </span>
            <button
              onClick={onBack}
              className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat group rounded-full"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>Back to canvas</span>
            </button>
            <span className="ns-t-small hidden sm:flex items-center gap-1.5"><ChevronRight size={12} aria-hidden="true" />Settings · Plan and credits</span>
            <span className="flex-1" />

            <ProfileMenu
              user={user}
              isPro={isPro}
              onAnalytics={onAnalytics}
              onSettings={onSettings}
              onSignOut={onSignOut}
            />
          </div>
        </header>

        {/* Main content: in the gutter and the measure of the bar; the plan is one section of Settings, so its navigation sits at the left */}
        <main className="ns-scroll-fade-top flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Page title */}
            <div className="flex items-center gap-3 mb-6">
              <span className="ns-mark ns-mark-lavender" aria-hidden="true">
                <CreditCard size={20} />
              </span>
              <div className="min-w-0">
                <h1 className="ns-t-page">Plan and credits</h1>
                <p className="text-sm text-ink-soft">Your plan, the credits that keep it, and the room you have</p>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6">
            <div className="lg:w-60 shrink-0">
              <SettingsNav active="plan" onSelect={(id) => { if (id !== 'plan') onSettings?.(); }} signedIn={!!user} isPro={isPro} />
            </div>
          <div className="flex-1 min-w-0 space-y-6">

            {/* Current Plan Status */}
            <section className={`ns-card ${isPro ? 'border-lavender/40 bg-lavender-soft/40' : ''}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
                <div className="flex items-start gap-4">
                  {/* The plan's mark: lavender on a paid plan, quiet on the free one */}
                  <span
                    className={`ns-mark ${isPro ? 'ns-mark-lavender' : ''}`}
                    data-tone={isPro ? undefined : 'ink'}
                    aria-hidden="true"
                  >
                    {isPro ? <Crown size={20} /> : <Shield size={20} />}
                  </span>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-xl font-semibold text-ink">
                        {isPro ? 'Pro plan' : 'Free plan'}
                      </h2>
                      {isPro && (
                        <span className="ns-badge ns-badge-ok">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-ink-soft">
                      {isPro
                        ? 'Unlimited nodes, cloud sync, and more'
                        : 'Limited to 3 nodes, local storage only'}
                    </p>
                  </div>
                </div>

                {/* Credits & Time */}
                {isPro && (
                  <div className="flex flex-wrap gap-3">
                    <div className="rounded-xl border border-cream-300 bg-white px-5 py-3">
                      <div className="ns-t-label flex items-center gap-1.5 mb-1">
                        <Coins size={12} />
                        <span>Credits left</span>
                      </div>
                      <p className="text-2xl font-semibold text-ink">{currentCredits}</p>
                    </div>

                    <div className="rounded-xl border border-cream-300 bg-white px-5 py-3">
                      <div className="ns-t-label flex items-center gap-1.5 mb-1">
                        <Clock size={12} />
                        <span>Current period</span>
                      </div>
                      <p className="text-2xl font-semibold text-ink">
                        {remainingDays > 0 ? `${remainingDays} days` : 'Expired'}
                      </p>
                    </div>

                    <div className="rounded-xl border border-lavender bg-lavender-soft px-5 py-3">
                      <div className="ns-t-label flex items-center gap-1.5 mb-1 text-lavender-deep">
                        <Zap size={12} />
                        <span>Total access</span>
                      </div>
                      <p className="text-2xl font-semibold text-lavender-deep">
                        {totalRemainingDays > 0 ? `${totalRemainingDays} days` : 'Expired'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Info about credit system */}
              {isPro && currentCredits > 0 && (
                <div className="ns-note mt-6 p-4 text-sm" data-tone="lavender">
                  <p>
                    <strong>How it works:</strong> When your current {remainingDays}-day period ends,
                    1 credit will be automatically used to start a new 30-day period.
                    You have <strong>{currentCredits} credits</strong> available for future months.
                  </p>
                </div>
              )}

              {/* Pro access given by hand: no credits are needed while it lasts */}
              {given && (
                <div className="ns-note mt-6 p-4 text-sm items-center" data-tone="ok">
                  <CheckCircle />
                  <p>
                    Pro access was given to this account{given.expiresAt ? <>, until <strong>{formatDate(given.expiresAt)}</strong></> : ''}.
                    {given.note ? ` ${given.note}` : ''} No credits are needed while it lasts.
                  </p>
                </div>
              )}

              {/* Expiry Warning - only show if running low on total time */}
              {!given && isPro && remainingDays <= 7 && remainingDays > 0 && currentCredits === 0 && (
                <div className="ns-note mt-6 p-4 text-sm items-center" data-tone="warn">
                  <AlertCircle />
                  <p>
                    Your Pro access expires in <strong>{remainingDays} days</strong> and you have no credits left.
                    Add more credits to maintain Pro access.
                  </p>
                </div>
              )}

              {/* Expired Warning */}
              {subscription?.currentCreditExpiresAt && remainingDays === 0 && currentCredits > 0 && (
                <div className="ns-note mt-6 p-4 text-sm items-center" data-tone="warn">
                  <AlertCircle />
                  <p>
                    Your current period has ended. You have <strong>{currentCredits} credits</strong> remaining.
                    Refresh the page to activate your next month of Pro access.
                  </p>
                </div>
              )}

              {/* Fully expired */}
              {subscription?.currentCreditExpiresAt && remainingDays === 0 && currentCredits === 0 && (
                <div className="ns-note mt-6 p-4 text-sm items-center" data-tone="danger">
                  <AlertCircle />
                  <p>
                    Your Pro access has expired and you have no credits remaining.
                    Purchase credits below to restore Pro features.
                  </p>
                </div>
              )}

              {/* Subscription Details */}
              {isPro && subscription?.currentCreditExpiresAt && (
                <div className="mt-6 pt-6 border-t border-cream-300">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-ink-soft mb-1">Current period ends</p>
                      <p className="text-ink font-medium">{formatDate(subscription.currentCreditExpiresAt)}</p>
                    </div>
                    <div>
                      <p className="text-ink-soft mb-1">Last credit used</p>
                      <p className="text-ink font-medium">
                        {subscription.lastCreditUsedAt ? formatDate(subscription.lastCreditUsedAt) : 'N/A'}
                      </p>
                    </div>
                    {subscription.solanaWalletAddress && (
                      <div>
                        <p className="text-ink-soft mb-1">Last paid from</p>
                        <p className="ns-t-mono text-ink">
                          {`${subscription.solanaWalletAddress.slice(0, 8)}…${subscription.solanaWalletAddress.slice(-8)}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Plan Features */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Current Plan Features */}
              <div className="ns-card">
                <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                  <Sparkles size={16} className="text-ink-soft" />
                  Your current features
                </h3>
                <ul className="space-y-3">
                  <FeatureItem
                    label="Workspaces"
                    value={limits.maxWorkspaces === -1 ? 'Unlimited' : limits.maxWorkspaces.toString()}
                    available={true}
                  />
                  <FeatureItem
                    label="Nodes per workspace"
                    value={limits.maxNodesPerWorkspace === -1 ? 'Unlimited' : limits.maxNodesPerWorkspace.toString()}
                    available={true}
                  />
                  <FeatureItem
                    label="Cloud sync"
                    value={limits.cloudSyncEnabled ? 'Enabled' : 'Disabled'}
                    available={limits.cloudSyncEnabled}
                  />
                  <FeatureItem
                    label="Cloud storage"
                    value={limits.maxStorageMB > 0 ? `${limits.maxStorageMB / 1024} GB` : 'None'}
                    available={limits.maxStorageMB > 0}
                  />
                  <FeatureItem
                    label="Video generation"
                    value={limits.videoGenerationEnabled ? 'Enabled' : 'Disabled'}
                    available={limits.videoGenerationEnabled}
                  />
                </ul>
              </div>

              {/* What Pro Unlocks (for free users) */}
              {!isPro && (
                <div className="ns-card border-lavender/40 bg-lavender-soft/40">
                  <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                    <Zap size={16} className="text-lavender-deep" />
                    Unlock with Pro
                  </h3>
                  <ul className="space-y-3">
                    <FeatureItem label="Unlimited workspaces" value="∞" available={true} highlighted />
                    <FeatureItem label="Unlimited nodes" value="∞" available={true} highlighted />
                    <FeatureItem label="Cloud sync" value="Real-time" available={true} highlighted />
                    <FeatureItem label="Storage" value="1 GB" available={true} highlighted />
                    <FeatureItem label="Cross-device access" value="Enabled" available={true} highlighted />
                  </ul>
                </div>
              )}

              {/* Storage Usage (for Pro users) */}
              {isPro && (
                <div className="ns-card">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                      <Database size={16} className="text-ink-soft" />
                      Usage and stats
                    </h3>
                    <button
                      onClick={handleRecalculateStorage}
                      disabled={isRecalculating}
                      className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat"
                      title="Scan Firebase Storage to recalculate actual usage"
                    >
                      <RefreshCw size={14} className={isRecalculating ? 'animate-spin' : ''} />
                      {isRecalculating ? 'Scanning...' : 'Recalculate'}
                    </button>
                  </div>
                  <div className="space-y-4">
                    {/* Storage Usage */}
                    <div>
                      <div className="flex justify-between gap-4 text-sm mb-2">
                        <span className="text-ink-soft">Cloud storage</span>
                        <span className="text-ink font-medium">
                          {(storageUsage.totalBytes / (1024 * 1024)).toFixed(2)} MB / {limits.maxStorageMB} MB
                        </span>
                      </div>
                      {/* Ink while there is room; the state's colour as it fills up */}
                      <div
                        className="ns-meter"
                        data-tone={storageRatio > 0.9 ? 'danger' : storageRatio > 0.7 ? 'warn' : undefined}
                        role="meter"
                        aria-label="Cloud storage used"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(storagePercent)}
                      >
                        <span style={{ width: `${storagePercent}%` }} />
                      </div>
                      <div className="ns-t-meta flex justify-between gap-4 mt-1.5">
                        <span>{storageUsage.fileCount} files uploaded</span>
                        <span>{(storageRatio * 100).toFixed(1)}% used</span>
                      </div>
                    </div>

                    {/* Recalculation Progress */}
                    {isRecalculating && recalculateProgress.total > 0 && (
                      <div className="ns-note flex-col items-stretch" data-tone="lavender">
                        <p className="flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" />
                          Scanning files: {recalculateProgress.current} / {recalculateProgress.total}
                        </p>
                        <div className="ns-meter" role="progressbar" aria-label="Scanning files" aria-valuemin={0} aria-valuemax={recalculateProgress.total} aria-valuenow={recalculateProgress.current}>
                          <span style={{ width: `${(recalculateProgress.current / recalculateProgress.total) * 100}%` }} />
                        </div>
                      </div>
                    )}

                    {/* Storage Warning */}
                    {storageRatio > 0.9 && (
                      <div className="ns-note" data-tone="danger">
                        <AlertCircle />
                        <p>
                          Storage almost full! Delete some files to continue uploading.
                        </p>
                      </div>
                    )}

                    {/* Storage Migration Section */}
                    {needsMigration && (
                      <div className="ns-note flex-col items-stretch gap-3 p-4" data-tone="warn">
                        <div className="flex items-start gap-3">
                          <FolderSync size={20} className="mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold">Storage migration available</h4>
                            <p className="mt-1">
                              Your storage uses the old folder structure. Migrate to the new organized format with folders for Images, Videos, Characters, etc.
                            </p>
                          </div>
                        </div>

                        {!isMigrating && !migrationResult && (
                          <button
                            onClick={handleMigrateStorage}
                            className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat w-full"
                          >
                            <FolderSync size={14} />
                            Migrate storage structure
                          </button>
                        )}

                        {isMigrating && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <Loader2 size={14} className="animate-spin" />
                              <span>{migrationProgress.status}</span>
                            </div>
                            {migrationProgress.total > 0 && (
                              <div>
                                <div className="ns-meter" role="progressbar" aria-label="Migrating files" aria-valuemin={0} aria-valuemax={migrationProgress.total} aria-valuenow={migrationProgress.current}>
                                  <span style={{ width: `${(migrationProgress.current / migrationProgress.total) * 100}%` }} />
                                </div>
                                <p className="mt-1 text-center">
                                  {migrationProgress.current} / {migrationProgress.total} files
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {migrationResult && (
                          <div className="ns-note flex-col items-stretch gap-1" data-tone={migrationResult.errors.length > 0 ? 'danger' : 'ok'}>
                            {migrationResult.migratedFiles > 0 && (
                              <p className="text-sm text-ok-deep flex items-center gap-2">
                                <Check size={14} />
                                Successfully migrated {migrationResult.migratedFiles} files!
                              </p>
                            )}
                            {migrationResult.errors.length > 0 && (
                              <div className="text-sm text-danger-deep">
                                <p className="flex items-center gap-2">
                                  <AlertCircle size={14} />
                                  {migrationResult.errors.length} error(s) occurred:
                                </p>
                                <ul className="mt-1 ml-5 text-xs list-disc">
                                  {migrationResult.errors.slice(0, 3).map((err, i) => (
                                    <li key={i}>{err}</li>
                                  ))}
                                  {migrationResult.errors.length > 3 && (
                                    <li>...and {migrationResult.errors.length - 3} more</li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Register Existing Files Section */}
                    <div className="ns-well p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Database size={20} className="text-ink-soft mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-ink">Register existing files</h4>
                          <p className="ns-t-small mt-1">
                            Scan your storage and register all files in the file registry. This enables stable file references that won't break when files are renamed.
                          </p>
                        </div>
                      </div>

                      {!isRegisteringFiles && !registerResult && (
                        <button
                          onClick={handleRegisterExistingFiles}
                          className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat w-full"
                        >
                          <Database size={14} />
                          Register existing files
                        </button>
                      )}

                      {isRegisteringFiles && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-ink-soft">
                            <Loader2 size={14} className="animate-spin" />
                            <span>{registerProgress.status}</span>
                          </div>
                          {registerProgress.total > 0 && (
                            <div>
                              <div className="ns-meter" role="progressbar" aria-label="Registering files" aria-valuemin={0} aria-valuemax={registerProgress.total} aria-valuenow={registerProgress.current}>
                                <span style={{ width: `${(registerProgress.current / registerProgress.total) * 100}%` }} />
                              </div>
                              <p className="ns-t-meta mt-1 text-center">
                                {registerProgress.current} / {registerProgress.total} files
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {registerResult && (
                        <div className="ns-note flex-col items-stretch gap-1" data-tone={registerResult.errors.length > 0 ? 'danger' : 'ok'}>
                          {(registerResult.registered > 0 || registerResult.skipped > 0) && (
                            <p className="text-sm text-ok-deep flex items-center gap-2">
                              <Check size={14} />
                              Registered {registerResult.registered} new files, skipped {registerResult.skipped} already registered
                            </p>
                          )}
                          {registerResult.errors.length > 0 && (
                            <div className="text-sm text-danger-deep">
                              <p className="flex items-center gap-2">
                                <AlertCircle size={14} />
                                {registerResult.errors.length} error(s):
                              </p>
                              <ul className="mt-1 ml-5 text-xs list-disc">
                                {registerResult.errors.slice(0, 3).map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="pt-2 border-t border-cream-200 space-y-3">
                      <div className="flex items-center justify-between gap-4 py-2 border-b border-cream-200 last:border-b-0">
                        <span className="text-sm text-ink-soft">Sync status</span>
                        <span className="ns-chip ns-chip-sm ns-chip-ok">
                          <span className="ns-tone-dot animate-pulse" data-tone="ok" />
                          <span>Real-time sync active</span>
                        </span>
                      </div>
                      {storageUsage.lastUpdated > 0 && (
                        <div className="flex items-center justify-between gap-4 py-2 border-b border-cream-200 last:border-b-0">
                          <span className="text-sm text-ink-soft">Last updated</span>
                          {/* How long ago, as the library says it; the exact time on hover */}
                          <span className="text-sm font-medium text-ink" title={new Date(storageUsage.lastUpdated).toLocaleString()}>
                            {formatRelativeTime(storageUsage.lastUpdated)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Available Plans - Dynamic from Admin */}
            {dynamicPlans.length > 0 && (
              <section className="ns-card p-0 overflow-hidden">
                <div className="p-6 border-b border-cream-200 bg-cream-50">
                  <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                    <Crown size={16} className="text-ink-soft" />
                    Available plans
                  </h3>
                  <p className="text-sm text-ink-soft mt-1">
                    Choose the plan that's right for you
                  </p>
                </div>
                <div className="p-6">
                  {plansLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-ink-faint" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {dynamicPlans.map((plan) => (
                        <PlanCard
                          key={plan.id}
                          plan={plan}
                          isCurrentPlan={plan.tier === tier}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Add Credits Section */}
            <section className="ns-card p-0 overflow-hidden">
              <div className="p-6 border-b border-cream-200 bg-cream-50">
                <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                  <CreditCard size={16} className="text-ink-soft" />
                  {isPro ? 'Add more credits' : 'Get Pro access'}
                </h3>
                <p className="text-sm text-ink-soft mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>1 credit = {PRO_MONTHLY_USDC} USDC = 1 month of Pro</span>
                  <span className="text-ink-faint line-through">{PRO_LIST_USDC} USDC</span>
                  <span className="ns-badge ns-badge-ok">{PRO_DISCOUNT_PERCENT}% off</span>
                </p>
              </div>

              <div className="p-6">
                {/* What was just paid */}
                {paid && (
                  <div className="ns-note mb-6 p-4 text-sm items-center" data-tone="ok" role="status">
                    <Check />
                    <div className="flex-1">
                      <p className="font-semibold">Payment received</p>
                      <p>{paid.credits} month{paid.credits === 1 ? '' : 's'} of Pro added to your account.</p>
                    </div>
                    <a
                      href={`https://explorer.solana.com/tx/${paid.signature}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat"
                    >
                      View transfer <ExternalLink size={14} />
                    </a>
                  </div>
                )}

                {/* Credit Packages */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  {CREDIT_PACKAGES.map((pkg, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setSelectedPackage(idx); setPaying(false); }}
                      aria-pressed={selectedPackage === idx}
                      className={`relative rounded-xl border p-4 transition-colors ${
                        selectedPackage === idx
                          ? 'border-lavender bg-lavender-soft'
                          : 'border-cream-300 bg-white hover:border-ink-faint'
                      }`}
                    >
                      {pkg.popular && (
                        <span className="ns-badge ns-badge-ink absolute -top-2 left-1/2 -translate-x-1/2">
                          Popular
                        </span>
                      )}
                      {pkg.best && (
                        <span className="ns-badge ns-badge-ink absolute -top-2 left-1/2 -translate-x-1/2">
                          Best value
                        </span>
                      )}
                      <div className="text-2xl font-semibold text-ink mb-1">{pkg.credits}</div>
                      <div className="text-xs text-ink-soft mb-2">{pkg.label}</div>
                      <div className="text-sm font-semibold text-ink">{formatUsdc(pkg.price)}</div>
                      <div className="text-xs text-ink-faint line-through">{formatUsdc(pkg.listPrice)}</div>
                    </button>
                  ))}
                </div>

                {/* Paying: the QR and the address for the chosen package, or the button that opens them */}
                {paying ? (
                  <div className="ns-well p-4">
                    <PayWithQr
                      credits={CREDIT_PACKAGES[selectedPackage].credits}
                      onPaid={({ signature, newCredits }) => {
                        void newCredits; // the record itself arrives through the subscription listener
                        setPaid({ signature, credits: CREDIT_PACKAGES[selectedPackage].credits });
                        setPaying(false);
                        onSubscriptionUpdate?.();
                      }}
                    />
                    <button type="button" onClick={() => setPaying(false)} className="ns-btn ns-btn-sm ns-btn-quiet mt-4">Not now</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setPaid(null); setPaying(true); }}
                    className="ns-btn ns-btn-primary ns-btn-flat w-full"
                  >
                    <Coins size={16} />
                    Pay {formatUsdc(CREDIT_PACKAGES[selectedPackage].price)}
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>

              {/* Payment Info */}
              <div className="px-6 py-4 bg-cream-50 border-t border-cream-200">
                <p className="ns-t-small text-center">
                  Pay in USDC on Solana mainnet from any wallet: scan the code or copy the address.
                  Credits are added as soon as the transfer is confirmed.
                </p>
              </div>
            </section>

            {/* FAQ / Help */}
            <section className="ns-card">
              <h3 className="text-lg font-semibold text-ink mb-4">Frequently asked questions</h3>
              <div className="space-y-4">
                <FAQItem
                  question="How do credits work?"
                  answer="Each credit gives you 1 month of Pro access. When your current month ends, a new credit is automatically consumed if available. You can add more credits anytime."
                />
                <FAQItem
                  question="What happens when I run out of credits?"
                  answer="Your account will revert to the Free plan with limited features (3 nodes max, no cloud sync). Your data will remain saved, and you can upgrade again anytime."
                />
                <FAQItem
                  question="Can I get a refund?"
                  answer="Due to the nature of blockchain transactions, we cannot process refunds. Please ensure you want to purchase before confirming the transaction."
                />
              </div>
            </section>

          </div>
          </div>
        </main>

        {/* Footer: the last line every page has, in the measure of this page */}
        <footer className="py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm">
          <div className="w-full flex items-center justify-between gap-3">
            <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
            {/* The links go through the app's router; drawn without one (a test, a harness) the line stands alone */}
            {inRouter && (
              <nav aria-label="Footer" className="flex items-center gap-1">
                <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
                <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
                <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
              </nav>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

// Plan Card - Shows a single subscription plan from the database
const PlanCard: React.FC<{ plan: SubscriptionPlan; isCurrentPlan: boolean }> = ({ plan, isCurrentPlan }) => {
  const isFree = plan.tier === 'free';
  const isEnterprise = plan.tier === 'enterprise';
  // The plan the account is on is the selected one: lavender. The others are plain cards.
  const surface = isCurrentPlan ? 'border-lavender bg-lavender-soft/40' : 'border-cream-300 bg-white';

  return (
    <div className={`relative rounded-xl border ${surface} p-5 flex flex-col`}>
      {/* Badge */}
      {plan.badge && (
        <span className="ns-badge ns-badge-ink absolute -top-2.5 left-4">
          {plan.badge}
        </span>
      )}
      {isCurrentPlan && (
        <span className="ns-badge ns-badge-lavender absolute -top-2.5 right-4">
          Current plan
        </span>
      )}

      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1 text-ink-soft">
          {isFree ? <Shield size={16} /> : isEnterprise ? <Sparkles size={16} /> : <Crown size={16} />}
          <h4 className="text-base font-semibold text-ink">{plan.displayName}</h4>
        </div>
        {plan.description && (
          <p className="text-sm text-ink-soft">{plan.description}</p>
        )}
      </div>

      {/* Price */}
      <div className="mb-4">
        <span className="text-2xl font-semibold text-ink">
          {plan.price === 0 ? 'Free' : `${plan.price} ${plan.currency}`}
        </span>
        {plan.price > 0 && (
          <span className="text-sm text-ink-soft"> / {plan.billingPeriod}</span>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-2 flex-1">
        <PlanFeatureRow
          label="Workspaces"
          value={plan.features.maxWorkspaces === -1 ? 'Unlimited' : String(plan.features.maxWorkspaces)}
          enabled={true}
        />
        <PlanFeatureRow
          label="Nodes per workspace"
          value={plan.features.maxNodesPerWorkspace === -1 ? 'Unlimited' : String(plan.features.maxNodesPerWorkspace)}
          enabled={true}
        />
        <PlanFeatureRow
          label="Cloud storage"
          value={plan.features.maxStorageMB > 0 ? `${plan.features.maxStorageMB >= 1024 ? `${(plan.features.maxStorageMB / 1024).toFixed(0)} GB` : `${plan.features.maxStorageMB} MB`}` : 'None'}
          enabled={plan.features.maxStorageMB > 0}
        />
        <PlanFeatureRow
          label="Cloud sync"
          value={plan.features.cloudSyncEnabled ? 'Enabled' : 'Disabled'}
          enabled={plan.features.cloudSyncEnabled}
        />
        <PlanFeatureRow
          label="Video generation"
          value={plan.features.videoGenerationEnabled ? 'Enabled' : 'Disabled'}
          enabled={plan.features.videoGenerationEnabled}
        />
      </ul>
    </div>
  );
};

// A feature of a plan and what the plan gives of it: the same row in a plan card and in the feature lists
const PlanFeatureRow: React.FC<{ label: string; value: string; enabled: boolean }> = ({ label, value, enabled }) => (
  <FeatureItem label={label} value={value} available={enabled} />
);

// Helper Components
const FeatureItem: React.FC<{
  label: string;
  value: string;
  available: boolean;
  highlighted?: boolean;
}> = ({ label, value, available, highlighted }) => (
  <li className="flex items-center justify-between gap-4 text-sm">
    <div className="flex items-center gap-2">
      {/* A check for what the plan has, a dash for what it does not: not an empty box, which reads as a checkbox */}
      {available ? (
        <Check size={14} className={`shrink-0 ${highlighted ? 'text-lavender-deep' : 'text-ok'}`} />
      ) : (
        <Minus size={14} className="shrink-0 text-ink-faint" />
      )}
      <span className={available ? 'text-ink-soft' : 'text-ink-faint'}>{label}</span>
    </div>
    <span className={`font-medium ${highlighted ? 'text-lavender-deep' : available ? 'text-ink' : 'text-ink-faint'}`}>
      {value}
    </span>
  </li>
);

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => (
  <div className="border-b border-cream-200 pb-4 last:border-0 last:pb-0">
    <h4 className="text-sm font-semibold text-ink mb-1">{question}</h4>
    <p className="text-sm text-ink-soft">{answer}</p>
  </div>
);

export default SubscriptionPage;
